import { memo, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Plane, Sparkles, Mountain, Sun, Trees } from "lucide-react";

interface HookTravelDealsProps {
  className?: string;
}

interface Deal {
  id: string;
  title: string;
  headline: string;
  route: string;
  price: string;
  savings: string;
  perks: string[];
  ctaLabel: string;
  ctaHref: string;
  icon: "sparkle" | "mountain" | "sun" | "forest";
  availability: string;
}

const iconMap = {
  sparkle: Sparkles,
  mountain: Mountain,
  sun: Sun,
  forest: Trees,
} as const;

export const HookTravelDeals = memo(function HookTravelDeals({ className }: HookTravelDealsProps) {
  const deals = useMemo<Deal[]>(
    () => [
      {
        id: "tokyo-sakura",
        title: "Cherry Blossom Escape",
        headline: "Spring in Tokyo with curated food tours",
        route: "JFK → HND",
        price: "from $899",
        savings: "Save up to 35%",
        perks: ["7-night boutique stay", "Guided street-food crawl", "Suica transit card"],
        ctaLabel: "View Tokyo Deal",
        ctaHref: "https://hooktravel.io/deals/tokyo-sakura",
        icon: "sparkle",
        availability: "Limited seats | March departures",
      },
      {
        id: "lisbon-coast",
        title: "Lisbon & Algarve Sun",
        headline: "Coastal road trip with space to unwind",
        route: "BOS → LIS",
        price: "from $779",
        savings: "Save $250 per traveler",
        perks: ["Convertible rental included", "Breakfast at oceanfront resort", "Sunset catamaran cruise"],
        ctaLabel: "Explore Portugal Deal",
        ctaHref: "https://hooktravel.io/deals/lisbon-algarve",
        icon: "sun",
        availability: "Flexible travel | April & May",
      },
      {
        id: "banff-adventure",
        title: "Banff Rockies Adventure",
        headline: "Guided hikes + hot springs recovery",
        route: "SEA → YYC",
        price: "from $645",
        savings: "Bundle & save 30%",
        perks: ["Small-group alpine guide", "Fairmont spa evening", "National park pass"],
        ctaLabel: "Grab Banff Bundle",
        ctaHref: "https://hooktravel.io/deals/banff-adventure",
        icon: "mountain",
        availability: "Only 12 bundles left",
      },
    ],
    []
  );

  return (
    <section className={cn("section-padding bg-muted/10", className)}>
      <div className="container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="outline" className="uppercase tracking-wide text-xs">
                Sponsored · Hook Travel
              </Badge>
              <Plane className="w-4 h-4 text-primary" />
            </div>
            <h2 className="text-3xl font-semibold">Travel deals exclusive to Listo users</h2>
            <p className="text-muted-foreground mt-2 text-sm md:text-base">
              Unlock limited-time itineraries curated by Hook Travel. Redeem with your Listo account and earn loyalty points for every booking.
            </p>
          </div>
          <Button variant="secondary" asChild>
            <a href="https://hooktravel.io/deals" target="_blank" rel="noopener noreferrer">
              View all Hook Travel offers
            </a>
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {deals.map((deal) => {
            const Icon = iconMap[deal.icon];
            return (
              <article key={deal.id} className="card-elegant p-6 border border-border/60">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="rounded-full bg-primary/10 p-2">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-xs uppercase tracking-wide text-primary font-semibold">
                      {deal.route}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">{deal.availability}</span>
                </div>

                <h3 className="text-lg font-semibold mb-1">{deal.title}</h3>
                <p className="text-sm text-muted-foreground mb-3">{deal.headline}</p>

                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-2xl font-semibold text-primary">{deal.price}</span>
                  <span className="text-xs text-emerald-500 font-medium">{deal.savings}</span>
                </div>

                <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                  {deal.perks.map((perk) => (
                    <li key={perk} className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>

                <Button size="sm" className="w-full" asChild>
                  <a href={deal.ctaHref} target="_blank" rel="noreferrer">
                    {deal.ctaLabel}
                  </a>
                </Button>
              </article>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground mt-6">
          Hook Travel is our trusted travel partner. Deals shown above refresh weekly based on availability. Prices are subject to change until booking is completed.
        </p>
      </div>
    </section>
  );
});

HookTravelDeals.displayName = "HookTravelDeals";
